import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../../context/AuthContext";
import FulfillmentDialog from "../FulfillmentDialog";
import api from "../../../services/api";

interface JobItem {
  id: string;
  taskId: string;
  studentId: string;
  studentName: string;
  taskTitle: string;
  priority: string;
  status: string;
  createdAt: string;
  expertId?: string;
}

export default function MarketingAssignedJobs({ refreshTrigger }: { refreshTrigger?: number }) {
  const navigate = useNavigate();
  const { data: session }: any = useSession();
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [fulfillmentItem, setFulfillmentItem] = useState<any | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const userId = session?.user?.id;

  const fetchJobs = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/service-pool?t=${Date.now()}`);
      const pool = Array.isArray(res.data) ? res.data : [];
      
      // Filter for items assigned specifically to the current associate
      const assigned = pool.filter((i: any) => 
        i.type === "EXPERT_JOB" && 
        i.status === "ASSIGNED" && 
        (i.expertId === userId || i.assignedToId === userId)
      );
      
      setJobs(assigned);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, [userId, refreshTrigger]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 10);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const amount = 400;
      scrollRef.current.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
    }
  };

  return (
    <div className="relative group animate-in fade-in slide-in-from-top-4 duration-700">
      <FulfillmentDialog 
        isOpen={!!fulfillmentItem} 
        onClose={() => setFulfillmentItem(null)}
        item={fulfillmentItem}
        onSuccess={fetchJobs}
      />

      <div className="flex items-center justify-between px-2 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shadow-sm border border-blue-500/20">
            <span className="material-symbols-outlined text-xl fill-1">work</span>
          </div>
          <div>
            <h3 className="text-xl font-black text-on-surface tracking-tighter">My Active Workspace</h3>
            <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest text-blue-500/60">Currently Under Execution</p>
          </div>
        </div>
      </div>

      {jobs.length > 0 ? (
        <>
          {/* Navigation Chevrons */}
          {showLeft && (
            <button 
              onClick={() => scroll("left")}
              className="absolute left-[-20px] top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-2xl border border-outline-variant/10 flex items-center justify-center hover:bg-slate-50 transition-all text-slate-400 group/btn cursor-pointer"
            >
              <span className="material-symbols-outlined group-hover/btn:text-blue-500">chevron_left</span>
            </button>
          )}
          {showRight && jobs.length > 2 && (
            <button 
              onClick={() => scroll("right")}
              className="absolute right-[-20px] top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-2xl border border-outline-variant/10 flex items-center justify-center hover:bg-slate-50 transition-all text-slate-400 group/btn cursor-pointer"
            >
              <span className="material-symbols-outlined group-hover/btn:text-blue-500">chevron_right</span>
            </button>
          )}

          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth p-6 -m-6 pb-12"
          >
            {jobs.map((job) => (
              <div 
                key={job.id}
                onClick={() => navigate(`/tasks/${job.taskId}?userId=${job.studentId}`)}
                className="flex-shrink-0 w-[400px] bg-surface-container-low border border-t-8 border-blue-500 rounded-[2.5rem] p-8 shadow-sm hover:shadow-2xl hover:translate-y-[-8px] transition-all flex flex-col justify-between gap-6 cursor-pointer relative overflow-hidden group/card"
              >
                <div className="absolute top-0 right-0 p-6 opacity-5 group-hover/card:opacity-20 transition-opacity">
                  <span className="material-symbols-outlined text-7xl fill-1 text-blue-600">person_play</span>
                </div>

                <div className="flex justify-between items-start relative">
                  <div>
                    <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Assigned Client</p>
                    <h4 className="text-2xl font-black text-on-surface tracking-tighter leading-tight group-hover/card:text-blue-600 transition-colors">{job.studentName}</h4>
                  </div>
                  <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-sm ${
                    job.priority === "VIP" ? "bg-cyan-500 text-white" :
                    job.priority === "PREMIUM" ? "bg-amber-500 text-white" :
                    "bg-slate-500 text-white"
                  }`}>
                    {job.priority}
                  </span>
                </div>

                <div className="space-y-4 relative">
                  <div className="h-px w-full bg-outline-variant/10" />
                  <div>
                    <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Execution Requirements</p>
                    <p className="text-sm font-bold text-on-surface leading-tight line-clamp-2">{job.taskTitle}</p>
                  </div>
                </div>

                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setFulfillmentItem({ ...job, type: "EXPERT_JOB" });
                  }}
                  className="w-full py-4 bg-blue-600 text-white rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:bg-blue-700 hover:translate-y-[-2px] active:scale-[0.98] transition-all cursor-pointer"
                >
                  Submit Execution Unit
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="w-full py-16 bg-surface-container-high/20 rounded-[3rem] border-2 border-dashed border-outline-variant/10 flex flex-col items-center justify-center gap-4 animate-in fade-in duration-700">
          <span className="material-symbols-outlined text-5xl text-blue-200 fill-1">work_outline</span>
          <p className="text-on-surface-variant font-medium italic opacity-60">Ready for assignment. Claim tickets from the Global Pool below.</p>
        </div>
      )}
    </div>
  );
}
